import { v4 as uuidv4 } from 'uuid';

interface Tree {
  id: string;
  uuid: string;
  name: string;
  supportedRevenue?: number;
  level?: number;
  percentage?: number;
  nodes?: Tree[];
}

export const generateUniqueId = (): string => {
  return uuidv4();
};

export const getNodes = (tree: any, position = { x: 0, y: 0 }, level = 0) => {
  const nodes:any = [];

  const traverse = (node: any, position: { x: number; y: number }, level: number) => {
    const nodeId = node?.id || generateUniqueId();
    nodes.push({
      id: nodeId,
      data: { ...node, id: nodeId },
      position: { x: position.x, y: position.y + level * 150 },
      type: 'workflow'
    });

    if (node?.nodes) {
      node.nodes.forEach((child: any, index: number) => {
        traverse(child, { x: position.x + index * 150, y: position.y + (level + 1) * 150 }, level + 1);
      });
    }
  };

  traverse(tree, position, level);
  return nodes;
};

export const getEdges = (tree: any) => {
  const edges:any = [];

  const traverse = (node: any) => {
    if (node?.nodes) {
      node.nodes.forEach((child: any) => {
        edges.push({
          id: `${node?.id}=>${child?.id}`,
          source: node?.id,
          target: child?.id,
          type: 'placeholder'
        });
        traverse(child);
      });
    }
  };

  traverse(tree);
  return edges;
};

export const getParentId = (nodeId: string, edges: any): string | null => {
  const parentEdge = edges.find((edge: any) => edge.target === nodeId);
  return parentEdge ? parentEdge.source : null;
};

export const getParentNode = (nodeId: string, nodes: any, edges: any) => {
  const parentEdge = edges.find((edge: any) => edge.target === nodeId);
  if (parentEdge) {
    const parentNode = nodes.find((node: any) => node.id === parentEdge.source);
    return parentNode || null;
  }
  return null;
};

export const findNodeById = (node: Tree, id: string): Tree | null => {
  if (node.id === id) return node;
  if (node.nodes) {
    for (let child of node.nodes) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
};

export const addChildrenToNodeWithParentValidation = (
  tree: Tree,
  parentId: string,
  targetNodeId: string,
  children: Tree[]
): Tree | null => {

  const addChildren = (node: Tree): boolean => {
    if (node.nodes) {
      for (let child of node.nodes) {
        if (child.id === targetNodeId) {
          if (child.level === 1 || node.id === parentId) {
            const existingUuids = new Set(child.nodes?.map(n => n.uuid));
            const filteredChildren = children.filter(c => !existingUuids.has(c.uuid));

            if (filteredChildren.length === 0) {
              console.log('All nodes to be added already exist in the target node.');
              return false;
            }

            child.nodes = child.nodes ? [...child.nodes, ...filteredChildren] : filteredChildren;
            return true;
          } else {
            console.log(`Validation failed: Node ${targetNodeId} is not a child of node ${parentId}`);
            return false;
          }
        }
        if (addChildren(child)) return true;
      }
    }
    return false;
  };

  const newTree = { ...tree };
  const parentExists = findNodeById(newTree, parentId);
  if (!parentExists) {
    console.log(`Parent node with ID ${parentId} not found.`);
    return null;
  }
  
  const success = addChildren(newTree);
  return success ? newTree : null;
};

export const isNodeAlreadyPopulated = (tree: Tree, nodeId: string): boolean => {
  const targetNode = findNodeById(tree, nodeId);
  return targetNode ? (targetNode.nodes ? targetNode.nodes.length > 0 : false) : false;
};

export const clearNodeChildrens = (
  tree: Tree,
  parentId: string,
  targetNodeId: string
): Tree | null => {


  const deleteChildren = (node: Tree): boolean => {
    if (node.nodes) {
      for (let child of node.nodes) {
        if (child.id === targetNodeId) {
          if (child.level === 1 || node.id === parentId) {
            child.nodes = [];
            return true;
          } else {
            console.log(`Validation failed: Node ${targetNodeId} is not a child of node ${parentId}`);
            return false;
          }
        }
        if (deleteChildren(child)) return true;
      }
    }
    return false;
  };

  const newTree = { ...tree };
  const parentExists = findNodeById(newTree, parentId);
  if (!parentExists) {
    console.log(`Parent node with ID ${parentId} not found.`);
    return null;
  }
  
  const success = deleteChildren(newTree);
  return success ? newTree : null;
};